/**
 * Dispatch Board API Client
 *
 * API functions for dispatch board and job assignment operations
 * All endpoints are authenticated and scoped to the service provider
 */

import { apiClient } from '@/services/api';
import type {
  DispatchBoard,
  JobAssignment,
  AssignJobRequest,
  ReassignJobRequest,
  UnassignJobRequest,
} from '../types/dispatch.types';

const API_BASE = '/provider/dispatch';

export const dispatchApi = {
  /**
   * Get dispatch board for provider
   */
  async getBoard(): Promise<DispatchBoard> {
    const response = await apiClient.get<{ board: DispatchBoard }>(`${API_BASE}/board`);
    return response.board;
  },

  /**
   * Initialize dispatch board (idempotent)
   */
  async initializeBoard(): Promise<string> {
    const response = await apiClient.post<{ data: { boardId: string } }>(`${API_BASE}/board/initialize`);
    return response.data.boardId;
  },

  /**
   * Add driver to board
   */
  async addDriver(driverName: string): Promise<void> {
    await apiClient.post(`${API_BASE}/board/drivers`, { driverName });
  },

  /**
   * Remove driver from board
   */
  async removeDriver(driverName: string): Promise<void> {
    const encodedName = encodeURIComponent(driverName);
    await apiClient.delete(`${API_BASE}/board/drivers/${encodedName}`);
  },

  /**
   * Get all job assignments
   */
  async getAssignments(driverId?: string): Promise<JobAssignment[]> {
    const queryString = driverId ? `?driverId=${encodeURIComponent(driverId)}` : '';
    const response = await apiClient.get<{ assignments: JobAssignment[] }>(`${API_BASE}/assignments${queryString}`);
    return response.assignments;
  },

  /**
   * Assign job to driver
   */
  async assignJob(request: AssignJobRequest): Promise<string> {
    const response = await apiClient.post<{ data: { assignmentId: string } }>(`${API_BASE}/assignments`, request);
    return response.data.assignmentId;
  },

  /**
   * Reassign job to different driver
   */
  async reassignJob(assignmentId: string, request: ReassignJobRequest): Promise<void> {
    await apiClient.put(`${API_BASE}/assignments/${assignmentId}/reassign`, request);
  },

  /**
   * Unassign job from driver
   */
  async unassignJob(jobId: string, request?: UnassignJobRequest): Promise<void> {
    await apiClient.delete(`${API_BASE}/assignments/${jobId}`, {
      body: request ? JSON.stringify(request) : undefined,
    });
  },

  /**
   * Update assignment priority
   */
  async updatePriority(assignmentId: string, priority: number): Promise<void> {
    await apiClient.patch(`${API_BASE}/assignments/${assignmentId}/priority`, { priority });
  },

  /**
   * Update assignment time
   */
  async updateTime(assignmentId: string, estimatedStartTime: string): Promise<void> {
    await apiClient.patch(`${API_BASE}/assignments/${assignmentId}/time`, { estimatedStartTime });
  },
};
